import { Asset, FolderContent } from '@/api/generatedApi';
import { CopiableGridColDef } from '@/types/copiableColumn';
import { getAssetFolderDirectory, getAssetName } from '@/utils/assets';
import { getCreatorEmailValue } from '@/utils/dataGridValueGetters';
import { dateFormat, formatMatchPercentage } from '@/utils/format';
import {
  RenderCreatedAt,
  RenderCreator,
  RenderDetails,
  RenderFolder,
  RenderName,
} from '../ContentsTable/cellDefinitions';
import { RenderPercentage } from '../Search/SearchTable/cellDefinitions';
import { MatchAsset } from '../Search/SearchTable';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { ReportTreeRow } from './ReportGroupTable';

const isMatchRow = (row: ReportTreeRow): boolean => row.rowType === 'match';

const RenderMatchPercentageCell = (parameters: GridRenderCellParams<ReportTreeRow>) => {
  const row = parameters.row;
  if (!isMatchRow(row) || row.matchPercentage === undefined) return;

  const sourceAsset = row.sourceAsset?.contentType === 'asset' ? (row.sourceAsset as Asset) : undefined;
  const matchAsset: MatchAsset = {
    ...row,
    contentType: 'asset',
    matchPercentage: row.matchPercentage,
  } as MatchAsset;

  return <RenderPercentage value={row.matchPercentage} searchAsset={sourceAsset} matchAsset={matchAsset} />;
};

export const matchPercentageColumn: CopiableGridColDef<ReportTreeRow> = {
  field: 'matchPercentage',
  headerName: 'Match %',
  width: 110,
  minWidth: 90,
  filterable: false,
  sortable: false,
  renderCell: RenderMatchPercentageCell,
  getCopyValue: row =>
    isMatchRow(row) && row.matchPercentage !== undefined ? `${formatMatchPercentage(row.matchPercentage)}%` : undefined,
};

export const reportTreeColumns: CopiableGridColDef<ReportTreeRow>[] = [
  {
    field: 'name',
    headerName: 'Name',
    width: 450,
    minWidth: 450,
    filterable: false,
    sortable: false,
    flex: 1,
    renderCell: parameters => RenderName(parameters as GridRenderCellParams<FolderContent>),
    getCopyValue: row => (row.contentType === 'asset' ? getAssetName(row as Asset) : row.name),
  },
  {
    field: 'details',
    headerName: 'Details',
    minWidth: 170,
    filterable: false,
    sortable: false,
    renderCell: parameters => RenderDetails(parameters as GridRenderCellParams<FolderContent>),
  },
  {
    field: 'folder',
    headerName: 'Folder',
    minWidth: 150,
    filterable: false,
    sortable: false,
    renderCell: parameters => RenderFolder(parameters as GridRenderCellParams<FolderContent>),
    getCopyValue: row => (row.contentType === 'asset' ? getAssetFolderDirectory(row as Asset) || undefined : undefined),
  },
  {
    field: 'createdAt',
    headerName: 'Created',
    width: 100,
    filterable: false,
    sortable: false,
    renderCell: parameters => RenderCreatedAt(parameters as GridRenderCellParams<FolderContent>),
    getCopyValue: row => (row.createdAt ? dateFormat(row.createdAt) : undefined),
  },
  {
    field: 'creator',
    headerName: 'Created By',
    minWidth: 130,
    flex: 2,
    filterable: false,
    sortable: false,
    renderCell: parameters => RenderCreator(parameters as GridRenderCellParams<FolderContent>),
    valueGetter: (value, row) => getCreatorEmailValue(value, row),
    getCopyValue: row => row.creator?.email,
  },
];
